<script lang="ts">
  import { Plus, X, Pencil, Check, Trash2 } from 'lucide-svelte';
  import Button from '../ui/button.svelte';
  import Card from '../ui/card.svelte';
  import { _ } from 'svelte-i18n';
  import { get } from 'svelte/store';
  import {
    filteredCategories,
    categoryActions,
    selectedTargetLanguage,
    flashcards,
    flashcardActions
  } from '../../stores/flashcards';
  import { navigationActions } from '../../stores/navigation';

  let {
    open = false,
    onClose,
  }: {
    open: boolean;
    onClose: () => void;
  } = $props();

  let newCategoryName = $state('');
  let editingId = $state<string | null>(null);
  let editingName = $state('');
  let errorMessage = $state<string | null>(null);
  let creating = $state(false);
  let savingCategoryId = $state<string | null>(null);
  let deletingCategoryId = $state<string | null>(null);

  const categoriesList = $derived($filteredCategories);
  const flashcardList = $derived($flashcards);
  const usageCounts = $derived.by(() => {
    const counts = new Map<string, number>();
    for (const card of flashcardList) {
      const id = card.category?.id;
      if (!id) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  });
  const languageName = $derived($selectedTargetLanguage?.name ?? '');

  const t = (key: string, options?: Record<string, unknown>) => get(_)(key, options);

  function resetState() {
    newCategoryName = '';
    editingId = null;
    editingName = '';
    errorMessage = null;
    creating = false;
    savingCategoryId = null;
    deletingCategoryId = null;
  }

  $effect(() => {
    if (!open) {
      resetState();
    }
  });

  async function handleCreate(event: Event) {
    event.preventDefault();
    if (creating) return;

    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      errorMessage = t('categories.dialog.errors.name_required');
      return;
    }

    if (!$selectedTargetLanguage) {
      errorMessage = t('categories.dialog.errors.language_required');
      return;
    }

    creating = true;
    errorMessage = null;

    try {
      await categoryActions.addCategory(trimmed, undefined, $selectedTargetLanguage);
      newCategoryName = '';
    } catch (error) {
      console.error('Failed to create category:', error);
      errorMessage = error instanceof Error ? error.message : t('categories.dialog.errors.create_failed');
    } finally {
      creating = false;
    }
  }

  function startEditing(category: { id: string; name: string }) {
    editingId = category.id;
    editingName = category.name;
    errorMessage = null;
  }

  function cancelEditing() {
    editingId = null;
    editingName = '';
  }

  async function handleUpdateCategory(id: string) {
    if (savingCategoryId) return;

    const trimmed = editingName.trim();
    if (!trimmed) {
      errorMessage = t('categories.dialog.errors.name_required');
      return;
    }

    savingCategoryId = id;
    errorMessage = null;

    try {
      await categoryActions.updateCategory(id, { name: trimmed });
      cancelEditing();
    } catch (error) {
      console.error('Failed to update category:', error);
      errorMessage = error instanceof Error ? error.message : t('categories.dialog.errors.update_failed');
    } finally {
      savingCategoryId = null;
    }
  }

  async function handleDeleteCategory(id: string) {
    if (deletingCategoryId) return;

    const count = usageCounts.get(id) ?? 0;
    const confirmed =
      count > 0
        ? confirm(t('categories.dialog.confirm_delete_with_usage', { values: { count } }))
        : confirm(t('categories.dialog.confirm_delete'));

    if (!confirmed) {
      return;
    }

    deletingCategoryId = id;
    errorMessage = null;

    try {
      await categoryActions.deleteCategory(id);
      if (editingId === id) {
        cancelEditing();
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      errorMessage = error instanceof Error ? error.message : t('categories.dialog.errors.delete_failed');
    } finally {
      deletingCategoryId = null;
    }
  }

  function handleCategoryClick(categoryId: string) {
    // Don't start learning if we're in edit mode
    if (editingId === categoryId) return;

    // Find the category object from categoriesList
    const category = categoriesList.find(cat => cat.id === categoryId);
    if (category) {
      // Select the category in the store
      flashcardActions.selectCategory(category);
    }

    // Start learning with this category
    const langId = $selectedTargetLanguage?.id;
    navigationActions.startFlashcards(langId, categoryId);

    // Close the dialog
    handleClose();
  }

    function handleClose() {
    onClose?.();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      handleClose();
    }
  }
</script>

{#if open}
  <div class="fixed inset-0 z-[96] flex flex-col bg-background"   
      role="dialog"
      aria-modal="true"
      tabindex="0"
      onkeydown={handleKeydown}
      >
    <div class="flex items-start justify-between px-6 pt-6 pb-4 gap-4">
      <div class="space-y-2 flex-1">
        <p class="text-xs uppercase tracking-[0.28em] text-muted-foreground break-words">{$_('categories.dialog.subtitle')}</p>
        <h2 class="text-2xl font-semibold text-foreground break-words">{languageName || $_('categories.dialog.title')}</h2>
        {#if languageName}
          <p class="text-sm text-muted-foreground">{$_('categories.dialog.selected_language', { language: languageName })}</p>
        {/if}
      </div>
      <button
        class="btn btn-close"
        onclick={onClose}
        aria-label={$_('aria.close_manage_categories')}
      >
        <X class="h-5 w-5" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 pb-24 space-y-6">
      {#if !$selectedTargetLanguage}
        <div class="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-200">
          {$_('categories.dialog.select_language_notice')}
        </div>
      {/if}

      {#if errorMessage}
        <div class="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {errorMessage}
        </div>
      {/if}

      <Card class="widget-card tone-header p-5">
        <form class="form-section sm:flex-row sm:items-center sm:gap-4" onsubmit={handleCreate}>
          <input
            type="text"
            placeholder={$_('categories.dialog.form_placeholder')}
            bind:value={newCategoryName}
            disabled={!$selectedTargetLanguage}
            class="form-input sm:flex-1"
          />
          <button type="submit" class="btn btn-primary w-full sm:w-auto" disabled={creating || !$selectedTargetLanguage}>
            {creating ? $_('categories.dialog.form_submitting') : $_('categories.dialog.form_submit')}
          </button>
        </form>
      </Card>

      <div class="space-y-3 pb-16">
        {#if categoriesList.length === 0}
          <Card class="widget-card tone-header p-6 text-center">
            <p class="text-sm">{$_('categories.dialog.empty')}</p>
          </Card>
        {:else}
          {#each categoriesList as category (category.id)}
            <Card
              class="widget-card tone-list p-3 relative cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lifted"
              onclick={() => handleCategoryClick(category.id)}
            >
              <div class="flex flex-col">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex-1 pr-12">
                    {#if editingId === category.id}
                      <input
                        type="text"
                        bind:value={editingName}
                        class="form-input text-sm"
                        onclick={(e) => e.stopPropagation()}
                      />
                      <p class="text-xs text-muted-foreground mt-1">
                        {$_('categories.dialog.usage', { values: { count: usageCounts.get(category.id) ?? 0 } })}
                      </p>
                    {:else}
                      <h3 class="text-sm font-semibold text-foreground">{category.name}</h3>
                      <p class="text-xs text-muted-foreground mt-1">
                        {$_('categories.dialog.usage', { values: { count: usageCounts.get(category.id) ?? 0 } })}
                      </p>
                    {/if}
                  </div>

                  <div class="absolute top-3 right-3 flex items-center gap-1">
                    {#if editingId === category.id}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        class="h-8 w-8 p-0 bg-primary/10 hover:bg-primary/20"
                        onclick={(e) => { e.stopPropagation(); handleUpdateCategory(category.id); }}
                        disabled={savingCategoryId === category.id}
                      >
                        <Check class="h-4 w-4 text-primary" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        class="h-8 w-8 p-0 hover:bg-muted"
                        onclick={(e) => { e.stopPropagation(); cancelEditing(); }}
                      >
                        <X class="h-4 w-4 text-muted-foreground" />
                      </Button>
                    {:else}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        class="h-8 w-8 p-0 hover:bg-primary/10"
                        onclick={(e) => { e.stopPropagation(); startEditing(category); }}
                      >
                        <Pencil class="h-4 w-4 text-foreground/60" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        class="h-8 w-8 p-0 hover:bg-destructive/20"
                        onclick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                        disabled={deletingCategoryId === category.id}
                      >
                        <Trash2 class="h-4 w-4 text-destructive" />
                      </Button>
                    {/if}
                  </div>
                </div>
              </div>
            </Card>
          {/each}
        {/if}
      </div>
    </div>
  </div>
{/if}
